import React, { useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import {
  Container, Paper, Typography, Box, Button, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, TextField, MenuItem,
  Tooltip, IconButton, FormControlLabel, Switch,
} from '@mui/material'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { getQorTransactions } from '../apollo/queries'
import { refundQorOrder, voidQorOrder, refreshQorSettlement } from '../apollo/mutations'

const GET_TXNS = gql`${getQorTransactions}`
const REFUND = gql`${refundQorOrder}`
const VOID = gql`${voidQorOrder}`
const REFRESH = gql`${refreshQorSettlement}`

const STATUS_COLOR = {
  PENDING: 'warning', SETTLED: 'success', COD_OWED: 'default',
  COD_COLLECTED: 'info', REFUNDED: 'error', VOIDED: 'error',
}
const STATUS_FILTERS = ['', 'PENDING', 'SETTLED', 'REFUNDED', 'VOIDED', 'COD_OWED', 'COD_COLLECTED']

export default function QorTransactions() {
  const [settlementStatus, setSettlementStatus] = useState('')
  const [includeRecurring, setIncludeRecurring] = useState(false)
  const [notice, setNotice] = useState(null)
  const [error, setError] = useState('')

  const { data, loading, refetch } = useQuery(GET_TXNS, {
    variables: { settlementStatus: settlementStatus || undefined, includeRecurring, limit: 100, offset: 0 },
    fetchPolicy: 'network-only',
  })

  const onDone = (verb) => (d) => {
    const r = d.refundQorOrder || d.voidQorOrder
    if (r?.ok) { setNotice(`${verb} OK — ${r.transactionId || ''} (${r.code || 'GW00'})`); setError('') }
    else { setError(r?.message || `${verb} failed.`) }
    setTimeout(() => setNotice(null), 4000)
    refetch()
  }
  const [refundMutation, { loading: refunding }] = useMutation(REFUND, {
    onCompleted: onDone('Refund'), onError: (e) => setError(e.message),
  })
  const [voidMutation, { loading: voiding }] = useMutation(VOID, {
    onCompleted: onDone('Void'), onError: (e) => setError(e.message),
  })
  const [refreshMutation, { loading: refreshing }] = useMutation(REFRESH, {
    onCompleted: (d) => {
      const r = d.refreshQorSettlement
      if (r?.ok) { setNotice(r.message || 'Settlement refreshed.'); setError('') }
      else { setError(r?.message || 'Could not refresh settlement.') }
      setTimeout(() => setNotice(null), 4000)
      refetch()
    },
    onError: (e) => setError(e.message),
  })

  const rows = data?.qorTransactions?.rows ?? []
  const count = data?.qorTransactions?.count ?? 0
  const busy = refunding || voiding || refreshing

  const copy = (v) => { if (v) navigator.clipboard?.writeText(v) }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>QorCommerce Transactions</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Every QorCommerce sale/refund/void with its gateway transaction ID. Use
          the Refund and Void actions to test those flows; the transaction IDs here
          are what QorPay&apos;s certification sheet asks for.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            select size="small" label="Settlement status" value={settlementStatus}
            onChange={(e) => setSettlementStatus(e.target.value)} sx={{ width: 220 }}
          >
            {STATUS_FILTERS.map((s) => <MenuItem key={s || 'all'} value={s}>{s || 'All'}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Switch checked={includeRecurring} onChange={(e) => setIncludeRecurring(e.target.checked)} />}
            label="Include Black Card renewals"
          />
          <Typography variant="caption" color="text.secondary">{count} total</Typography>
        </Box>

        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {loading ? <CircularProgress size={24} /> : (
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Restaurant</TableCell>
                <TableCell>Transaction ID</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Settlement</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No transactions yet.</Typography></TableCell></TableRow>
              )}
              {rows.map((t) => {
                const isRecurring = t.source && t.source !== 'ORDER'
                // Recurring/membership charges have no order to void/refund here.
                const terminal = t.settlementStatus === 'REFUNDED' || t.settlementStatus === 'VOIDED'
                const isQorOrder = !isRecurring && t.paymentMethod === 'QOR' && !!t.qorTransactionId
                // Void only pre-settlement (PENDING); refund only post-settlement (SETTLED).
                const canVoid = isQorOrder && !terminal && t.settlementStatus === 'PENDING'
                const canRefund = isQorOrder && !terminal && t.settlementStatus === 'SETTLED'
                const canRefresh = isQorOrder && !terminal
                return (
                  <TableRow key={t._id} hover sx={isRecurring ? { bgcolor: 'rgba(255,193,7,0.06)' } : undefined}>
                    <TableCell>
                      {t.orderNumber || t.orderId}
                      {isRecurring && <Chip size="small" label="Black Card" sx={{ ml: 0.5, height: 18 }} />}
                    </TableCell>
                    <TableCell>{t.restaurantName || t.restaurantId}</TableCell>
                    <TableCell>
                      {t.qorTransactionId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{t.qorTransactionId}</Typography>
                          <Tooltip title="Copy"><IconButton size="small" onClick={() => copy(t.qorTransactionId)}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                        </Box>
                      ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell>{t.cardBrand || '—'}</TableCell>
                    <TableCell align="right">${Number(t.grandTotal).toFixed(2)}</TableCell>
                    <TableCell>{t.paymentMethod}</TableCell>
                    <TableCell><Chip size="small" color={STATUS_COLOR[t.settlementStatus] || 'default'} label={t.settlementStatus} /></TableCell>
                    <TableCell>{t.paymentStatus || '—'}</TableCell>
                    <TableCell><Typography variant="caption">{t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}</Typography></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {canRefresh && (
                          <Tooltip title="Re-check settlement status from QorCommerce">
                            <Button size="small" variant="text" disabled={busy}
                              onClick={() => refreshMutation({ variables: { orderId: t.orderId } })}>Refresh</Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Void: cancel before settlement (PENDING only)">
                          <span>
                            <Button size="small" variant="outlined" color="warning" disabled={!canVoid || busy}
                              onClick={() => voidMutation({ variables: { orderId: t.orderId } })}>Void</Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Refund: reverse after settlement (SETTLED only)">
                          <span>
                            <Button size="small" variant="outlined" color="error" disabled={!canRefund || busy}
                              onClick={() => refundMutation({ variables: { orderId: t.orderId } })}>Refund</Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          </Box>
        )}
      </Paper>
    </Container>
  )
}
